import {PersistentCityModel} from "@/models/PersistentCityModel";
import moment from "moment/moment";
import {Property} from "@/models/Property";
import {toMoment} from "@/dateUtils";
import {postApi, requestApi} from "@/api";
import {
  compareLicensesByAddress as compareLicensesByAddressDomain,
  compareLicensesByStatus as compareLicensesByStatusDomain,
  compareLicensesByDateIssued as compareLicensesByDateIssuedDomain,
  compareLicensesByDateApplied as compareLicensesByDateAppliedDomain,
  compareLicensesByDateExpires as compareLicensesByDateExpiresDomain,
  computeWorkflowStatus,
  isLicenseLifecycleActive,
  isLicenseLifecycleInactive,
  isLicenseLifecyclePending,
  resolveLicenseStatus,
  UNSPECIFIED_LICENSE_TYPE,
  licensePassesFilter as licensePassesFilterDomain
} from "@strenforcement/domain/license";
import { isLicenseExpired } from '@strenforcement/domain/licenseExpiration';
import { licenseDatesEqual, normalizeLicenseDay } from '@strenforcement/domain/licenseDates';
import { getLicenseAppliedOn } from '@/data/licenseDates';
import {licenseRepository} from "@/data";

/**
 * Filter helper for licenses. Kept in this file because it depends on
 * license.getCustomFieldValue() which is a License model method.
 */
function licensePassesFilter(license, {
  statusAllowlist = {},
  licenseTypeAllowlist = {},
  onlyShowMissingProperties = false,
  customFieldOptions = {}
}) {
  return licensePassesFilterDomain({
    status: license?.status,
    licenseType: license?.licenseType,
    hasProperty: !!license?.property,
    getCustomFieldValue: fieldId => license.getCustomFieldValue(fieldId),
    statusAllowlist,
    licenseTypeAllowlist,
    onlyShowMissingProperties,
    customFieldOptions
  });
}

export class LicenseFilter {

  constructor(/* City */ city, /* LicenseFilter */ copyFrom) {
    this.city = city;
    this.status = copyFrom ? {...copyFrom.status} : {};
    this.onlyShowMissingProperties = copyFrom ? copyFrom.onlyShowMissingProperties : false;
    this.sortField = copyFrom ? copyFrom.sortField : 'status';
    this.sortAscending = copyFrom ? copyFrom.sortAscending : false;

    const configuredLicenseTypes = (this.city.licenseTypes || [])
    .filter(type => type?.code)
    .map(type => ({ value: type.code, label: type.label || type.code }));
    this.licenseTypeOptions = configuredLicenseTypes.length === 0
      ? []
      : [
          ...configuredLicenseTypes,
          { value: UNSPECIFIED_LICENSE_TYPE, label: 'Unspecified' }
        ];
    this.licenseType = {};
    this.licenseTypeOptions.forEach(type => {
      this.licenseType[type.value] = copyFrom?.licenseType?.[type.value] ?? true;
    });

    this.customFields = this.city.customFields.filter(field => field.filter);
    this.customFields.forEach(field => {
      this[field.id] = {};
      field.options.forEach(option => {
        this[field.id][option] = copyFrom ? copyFrom[field.id][option] : true;
      });
    });

    // Set default values for license status filters
    let status = {};
    this.statusOptions = this.city.licenseStatuses;
    this.city.licenseStatuses.forEach(s => {
      status[s.code] = copyFrom ? copyFrom.status[s.code] : s.type !== 'inactive';
    });
    this.status = status;
  }

  get customFieldOptionMap() {
    let map = {};
    this.customFields.forEach(field => {
      map[field.id] = this[field.id];
    });
    return map;
  }

  copy() {
    return new LicenseFilter(this.city, this);
  }

  filter(/* License */ license) {
    return licensePassesFilter(license, {
      statusAllowlist: this.status,
      licenseTypeAllowlist: this.licenseType,
      onlyShowMissingProperties: this.onlyShowMissingProperties,
      customFieldOptions: this.customFieldOptionMap
    });
  }

  reset(statusFilter) {
    this.city.licenseStatuses.forEach(s => {
      this.status[s.code] = statusFilter ? s.type === statusFilter : s.type !== 'inactive';
    });
  }
}

export const compareLicensesByStatus = compareLicensesByStatusDomain;
export const compareLicensesByAddress = compareLicensesByAddressDomain;
export const compareLicensesByDateIssued = compareLicensesByDateIssuedDomain;
export const compareLicensesByDateApplied = compareLicensesByDateAppliedDomain;
export const compareLicensesByDateExpires = compareLicensesByDateExpiresDomain;

export class License extends PersistentCityModel {

  static collectionName = 'licenses';

  static async searchByLicenseIdPrefix(city, licenseIdPrefix, { maxResults = 25 } = {}) {
    licenseIdPrefix = String(licenseIdPrefix || '').trim();
    if (!licenseIdPrefix) {
      return [];
    }

    const { results } = await this.query(city, {
      constraints: [
        ['licenseId', '>=', licenseIdPrefix],
        ['licenseId', '<', `${licenseIdPrefix}\uf8ff`]
      ],
      sort: 'licenseId',
      sortDirection: 'asc',
      maxResults
    });

    return results;
  }

  static createDraftForProperty(property, additionalData = {}) {
    return !property ? null : new License(property.city, {
      data: {
        propertyId: property.id,
        contact: {
          owners: property.owners || [],
          address: property.details && property.details.ownerAddress
        },
        ...additionalData
      }
    });
  }

  constructor(city, data) {
    super(city, data);
    this.approvalNotification = null;
    this._data = {
      propertyId: null,
      property: null,
      contact: {
        owners: [],
        address: null,
        email: '',
        phone: '',
      },
      address: null, // Unverified address - use as a hint to the user when linking this to a property
      listings: [],
      customFieldValues: [],
      dateApplied: null,
      status: null,
      paid: false,
      licenseId: null,
      licenseType: null,
      dateIssued: null,
      ...this._data
    };
  }

  get number() {
    return this.data.licenseId || this.id;
  }

  get licenseType() {
    return this.data.licenseType || null;
  }

  get licenseTypeLabel() {
    if (!this.licenseType) return null;
    return this.city?.licenseTypes?.find(type => type.code === this.licenseType)?.label
      || this.licenseType;
  }

  async updateLicenseId(licenseId, note) {
    const response = await licenseRepository.updateLicenseId(this.city.id, this.id, {
      licenseId,
      ...(note !== undefined ? { note } : {})
    });
    return await this._applyUpdateResponse(response);
  }

  get status() {
    return computeWorkflowStatus({
      workflowStatus: this.data.status,
      isExpired: this.isExpired
    });
  }

  get statusWithConfig() {
    const { status } = resolveLicenseStatus({
      workflowStatus: this.status,
      isExpired: this.isExpired,
      statusConfig: this.statusOptions
    });
    return status;
  }

  get statusOptions() {
    // TODO We might want to restrict this according to the current status
    return this.city.licenseStatuses;
  }

  // Preserve the precise application instant independently of the editable calendar day.
  get dateApplied() {
    return this.data.dateApplied;
  }

  get appliedOn() {
    return getLicenseAppliedOn(this.data);
  }

  set appliedOn(value) {
    this.data.appliedOn = normalizeLicenseDay(value, 'appliedOn');
  }

  /* @deprecated Use issuedOn */
  get dateIssued() {
    return this.data.issuedOn ?? this.data.dateIssued;
  }

  get issuedOn() {
    return this.data.issuedOn ?? this.data.dateIssued;
  }

  get canRenew() {
    return this.isExpired && !this.isDeleted;
  }

  get dateAppliedFormatted() {
    const applied = toMoment(this.appliedOn);
    return applied ? applied.format('M/D/YY') : null;
  }

  get dateIssuedFormatted() {
    const issued = toMoment(this.issuedOn);
    return issued ? issued.format('M/D/YY') : null;
  }

  get expirationDateFormatted() {
    const date = this.expirationDate;
    return date ? moment(date).format('M/D/YY') : null;
  }

  get expirationDate() {
    return toMoment(this.data.expiresOn)?.toDate() || null;
  }

  get expiresOn() {
    return this.data.expiresOn || null;
  }

  get isExpired() {
    return isLicenseExpired(this.data.expiresOn);
  }

  get isDeleted() {
    return this.status === 'deleted';
  }

  get isClosed() {
    return this.status === 'closed';
  }

  get isWithdrawn() {
    return this.status === 'withdrawn';
  }

  get isIssued() {
    return this.status === 'issued';
  }

  get isPending() {
    return isLicenseLifecyclePending({
      workflowStatus: this.data.status,
      isExpired: this.isExpired,
      statusConfig: this.statusOptions
    });
  }

  get isActive() {
    return isLicenseLifecycleActive({
      workflowStatus: this.data.status,
      isExpired: this.isExpired,
      statusConfig: this.statusOptions
    });
  }

  get isInactive() {
    return isLicenseLifecycleInactive({
      workflowStatus: this.data.status,
      isExpired: this.isExpired,
      statusConfig: this.statusOptions
    });
  }

  get isSuspended() {
    return this.status === 'suspended';
  }

  get contact() {
    return this.data.contact || {};
  }

  get address() {
    const property = this.property;
    return property ? property.address : null;
  }

  get unverifiedAddress() {
    return this.data.address;
  }

  get addressFormatted() {
    const property = this.property;
    return property ? property.addressFormatted : null;
  }

  get addressStreetNumber() {
    return this.property?.address?.streetNumber;
  }

  get addressStreetName() {
    return this.property?.address?.street;
  }

  get addressUnit() {
    return this.property?.address?.unit;
  }

  get isLinkedToProperty() {
    return !!this.propertyId;
  }

  get propertyId() {
    return this.data.propertyId || this.data.property?.id;
  }

  set propertyId(propertyId) {
    this.data.propertyId = propertyId;
  }

  get property() {
    const property = this.data.property;
    let propertyId = this.propertyId;
    if (property && !propertyId) {
      // Default property ID to the ID in the nested property object
      propertyId = property.id;
    }
    return property && propertyId ? Property.dataConverter(this.city)({
      id: propertyId,
      data: property
    }) : null;
  }

  set property(property) {
    this.data.property = property ? property._data : null;
  }

  get imageUrl() {
    const property = this.property;
    return property && property.imageUrl;
  }

  get thumbnailUrl() {
    return this.property?.thumbnailUrl || null;
  }

  get latlng() {
    const property = this.property;
    return property && property.latlng;
  }

  get listings() {
    return this.data.listings || [];
  }

  indexKeys() {
    const address = (this.addressFormatted || '');
    const title = (this.number || '');
    const contact = ((this.contact || {}).owners || []).join(' ');
    return (this.id + ' ' + address + ' ' + title + ' ' + contact).toLowerCase()
    .split(/ /g)
    .map(word => word.trim())
    .filter(word => !!word);
  }

  approvalEmailApiPath(action) {
    return `/v1/cities/${encodeURIComponent(this.city.id)}/${this.constructor.collectionName}/${encodeURIComponent(this.id)}/approval-email/${action}`;
  }

  previewApprovalEmail() {
    return requestApi(this.approvalEmailApiPath('preview'));
  }

  async getApprovalEmailDelivery() {
    const response = await requestApi(this.approvalEmailApiPath('delivery'));
    return response?.delivery || null;
  }

  sendApprovalEmail() {
    return postApi(this.approvalEmailApiPath('send'));
  }

  sendApprovalEmailTest() {
    return postApi(this.approvalEmailApiPath('test'));
  }

  statusEmailApiPath(action = '', ruleId = null) {
    const base = `/v1/cities/${encodeURIComponent(this.city.id)}/${this.constructor.collectionName}/${encodeURIComponent(this.id)}/status-emails`;
    const rulePath = ruleId ? `/${encodeURIComponent(ruleId)}` : '';
    return `${base}${rulePath}${action ? `/${action}` : ''}`;
  }

  async getStatusEmailDeliveries() {
    const response = await requestApi(this.statusEmailApiPath('deliveries'));
    return response?.deliveries || [];
  }

  previewStatusEmail(ruleId) {
    return requestApi(this.statusEmailApiPath('preview', ruleId));
  }

  sendStatusEmail(ruleId) {
    return postApi(this.statusEmailApiPath('send', ruleId));
  }

  sendStatusEmailTest(ruleId) {
    return postApi(this.statusEmailApiPath('test', ruleId));
  }

  async delete(permanent) {
    const basePath = `/v1/cities/${encodeURIComponent(this.city.id)}/${this.constructor.collectionName}/${encodeURIComponent(this.id)}`;
    if (permanent) {
      await requestApi(basePath, { method: 'DELETE' });
      this.updateCount++;
      return this;
    } else {
      const response = await postApi(`${basePath}/delete`);
      if (response?.license?.data) {
        this._data = response.license.data;
        this.updateCount++;
        return this;
      }
      return await this.reload();
    }
  }

  get originalIssueDate() {
    return this._get('originalIssueDate');
  }

  async renew({ emailNotificationDecisions } = {}) {
    const response = await postApi(`/v1/cities/${encodeURIComponent(this.city.id)}/${this.constructor.collectionName}/${encodeURIComponent(this.id)}/renew`, {
      ...(emailNotificationDecisions ? { emailNotificationDecisions } : {})
    });
    this.emailNotifications = response?.emailNotifications || [];
    if (response?.license?.data) {
      this._data = response.license.data;
      this.updateCount++;
      return this;
    }
    return await this.reload();
  }

  getCustomFieldValue(id) {
    const fv = this._data.customFieldValues.filter(fv => fv.id === id)[0];
    return (fv && fv.value) || null;
  }

  get customFieldData() {
    let data = {};
    this._data.customFieldValues.forEach(fv => {
      data[fv.id] = fv.value;
    });
    return data;
  }

  async _applyUpdateResponse(response) {
    this.approvalNotification = response?.approvalNotification || null;
    this.emailNotifications = response?.emailNotifications || [];
    const updated = response?.license?.data;
    if (updated) {
      this._data = {
        ...this._data,
        ...updated
      };
      this.updateCount++;
      return this;
    }
    return await this.reload();
  }

  async _updateWithNote(
    data = {},
    note = null,
    approvalNotificationDecision,
    emailNotificationDecisions
  ) {
    const response = await licenseRepository.updateLicense(this.city.id, this.id, {
      data,
      note,
      ...(approvalNotificationDecision ? { approvalNotificationDecision } : {}),
      ...(emailNotificationDecisions ? { emailNotificationDecisions } : {})
    });
    return await this._applyUpdateResponse(response);
  }

  async updatePropertyId({ propertyId, note = null } = {}) {
    const data = {};
    if (propertyId !== undefined && propertyId !== this.propertyId) {
      data.propertyId = propertyId;
    }

    const message = String(note || '').trim();
    if (Object.keys(data).length === 0 && !message) {
      return this;
    }

    return await this._updateWithNote(data, message || null);
  }

  async updateStatusAndDates({
    status,
    appliedOn,
    issuedOn,
    note,
    approvalNotificationDecision,
    emailNotificationDecisions
  } = {}) {
    const data = {};
    if (status !== undefined) {
      data.status = status;
    }
    if (appliedOn !== undefined && !licenseDatesEqual(appliedOn, this.appliedOn, { dateOnly: true })) {
      data.appliedOn = normalizeLicenseDay(appliedOn, 'appliedOn');
    }
    if (issuedOn !== undefined && !licenseDatesEqual(issuedOn, this.issuedOn, { dateOnly: true })) {
      data.issuedOn = issuedOn;
    }

    const message = String(note || '').trim();
    if (Object.keys(data).length === 0 && !message) {
      return this;
    }

    return await this._updateWithNote(
      data,
      message || null,
      approvalNotificationDecision,
      emailNotificationDecisions
    );
  }

  async updateContact({ names, address, email, phone, note } = {}) {
    const owners = names === undefined
      ? (this.contact?.owners || [])
      : (Array.isArray(names)
          ? names.map(name => String(name || '').trim()).filter(Boolean)
          : []);

    const data = {
      contact: {
        owners,
        address: address === undefined ? (this.contact?.address || null) : (address || null),
        email: email === undefined ? (this.contact?.email || null) : (email || null),
        phone: phone === undefined ? (this.contact?.phone || null) : (phone || null)
      }
    };

    const message = String(note || '').trim();
    return await this._updateWithNote(data, message || null);
  }

  async updateApplicationAnswers(changes, note = null) {
    const response = await licenseRepository.updateLicenseApplicationAnswers(
      this.city.id,
      this.id,
      {
        changes,
        note: String(note || '').trim() || null
      }
    );
    return await this._applyUpdateResponse(response);
  }
}
